import { ColumnDef } from '@tanstack/react-table'

import { ActivityLogV2 } from '@/catalog-apps/components/SupportPanel/DataAssetsV2/ActivityLogV2/ActivityLogV2'
import { useDownloadFile } from '@/catalog-apps/components/SupportPanel/TemplatesV2/useDownloadFile'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { ComposedSheet } from '@/components/ui/ComposedSheet'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { TooltipWrapper } from '@/components/ui/TooltipWrapper'
import { MessagesStorageStorageFileType } from '@/util/enums'
import { IconArrowRightOutline } from '@icons/IconArrowRightOutline'
import { IconListFormatOutline } from '@icons/IconListFormatOutline'
import { IconPaperclipOutline } from '@icons/IconPaperclipOutline'
import { IconTransformOutline } from '@icons/IconTransformOutline'

import {
  DataAssetSummaryRow,
  getDataAssetName,
  getDateValue,
  getEntityName,
  getEntityTypeName,
  getFileName,
  getFileUrl,
  getStateValue,
  normalizeValue,
} from './workspaceDataAssetsTableUtils'

const dateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
const timeFormatter = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' })

const multiSelectFilter = (
  row: { getValue: <TValue>(columnId: string) => TValue },
  columnId: string,
  filterValue: string[]
) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true
  return filterValue.includes(normalizeValue(row.getValue<string | null | undefined>(columnId)))
}

function EmptyValue() {
  return <span className="text-muted-foreground">-</span>
}

function FileNameCell({ item }: { item: DataAssetSummaryRow }) {
  const { mutate: downloadFile, isPending } = useDownloadFile()
  const fileName = getFileName(item)
  const fileUrl = getFileUrl(item)

  if (!fileName) return <EmptyValue />
  if (!fileUrl) return <span className="break-words">{fileName}</span>

  return (
    <TooltipButton tooltip={`Download '${fileName}'`}>
      <Button
        variant="link"
        className="h-auto w-full justify-start truncate p-0 text-left"
        onClick={() =>
          downloadFile({
            url: fileUrl,
            fileName,
            fileType: MessagesStorageStorageFileType.Result,
          })
        }
        disabled={isPending}
      >
        <IconPaperclipOutline size={16} className="mr-1 shrink-0 text-foreground" />
        <span className="truncate font-light italic">{fileName}</span>
        {isPending && <IconTransformOutline size={16} className="ml-1 shrink-0 text-foreground" />}
      </Button>
    </TooltipButton>
  )
}

function ActivityLogActionCell({ item }: { item: DataAssetSummaryRow }) {
  const entityId = item.entity?.id
  const dataAssetId = item.data_asset?.id

  if (!entityId || !dataAssetId) return <EmptyValue />

  return (
    <div className="flex justify-center">
      <TooltipWrapper
        tooltip="Actions & activity log"
        wrapTrigger
        triggerComponent={
          <ComposedSheet
            description={
              <span className="flex items-center gap-2">
                {getEntityTypeName(item) || 'Unknown Type'}
                <IconArrowRightOutline size={10} />
                {getEntityName(item) || 'Unknown Entity'}
                <IconArrowRightOutline size={10} />
                {getDataAssetName(item) || 'Unknown Data Asset'}
              </span>
            }
            title="Actions & activity log"
            wide
            trigger={undefined}
          >
            <ActivityLogV2 entityId={entityId} dataAssetId={dataAssetId} />
          </ComposedSheet>
        }
      >
        <Button variant="ghost" size="icon" className="h-5 w-5">
          <IconListFormatOutline size={16} />
          <span className="sr-only">Open actions and activity log</span>
        </Button>
      </TooltipWrapper>
    </div>
  )
}

export const workspaceDataAssetsTableColumns: ColumnDef<DataAssetSummaryRow>[] = [
  {
    id: 'entityTypeName',
    accessorFn: getEntityTypeName,
    header: ({ column }) => <SortingHeaderButton column={column}>Entity Type</SortingHeaderButton>,
    cell: ({ row }) => getEntityTypeName(row.original) || <EmptyValue />,
    sortingFn: (rowA, rowB) => getEntityTypeName(rowA.original).localeCompare(getEntityTypeName(rowB.original)),
    filterFn: multiSelectFilter,
    enableGlobalFilter: true,
    size: 140,
  },
  {
    id: 'dataAssetName',
    accessorFn: getDataAssetName,
    header: ({ column }) => <SortingHeaderButton column={column}>Data Asset</SortingHeaderButton>,
    cell: ({ row }) => getDataAssetName(row.original) || <EmptyValue />,
    sortingFn: (rowA, rowB) => getDataAssetName(rowA.original).localeCompare(getDataAssetName(rowB.original)),
    filterFn: multiSelectFilter,
    enableGlobalFilter: true,
    size: 220,
  },
  {
    id: 'entityName',
    accessorFn: getEntityName,
    header: ({ column }) => <SortingHeaderButton column={column}>Entity Name</SortingHeaderButton>,
    cell: ({ row }) => getEntityName(row.original) || <EmptyValue />,
    sortingFn: (rowA, rowB) => getEntityName(rowA.original).localeCompare(getEntityName(rowB.original)),
    filterFn: multiSelectFilter,
    enableGlobalFilter: true,
    size: 220,
  },
  {
    id: 'fileName',
    accessorFn: getFileName,
    header: ({ column }) => <SortingHeaderButton column={column}>File Name</SortingHeaderButton>,
    cell: ({ row }) => <FileNameCell item={row.original} />,
    sortingFn: (rowA, rowB) => getFileName(rowA.original).localeCompare(getFileName(rowB.original)),
    enableGlobalFilter: true,
    size: 260,
  },
  {
    id: 'date',
    accessorFn: getDateValue,
    header: ({ column }) => <SortingHeaderButton column={column}>Date</SortingHeaderButton>,
    cell: ({ row }) => {
      const dateValue = getDateValue(row.original)
      if (!dateValue) return <EmptyValue />

      const parsedDate = new Date(dateValue)
      if (Number.isNaN(parsedDate.getTime())) {
        return dateValue
      }

      return (
        <div className="flex flex-col">
          <span>{dateFormatter.format(parsedDate)}</span>
          <span className="text-xs text-muted-foreground">{timeFormatter.format(parsedDate)}</span>
        </div>
      )
    },
    sortingFn: (rowA, rowB) => getDateValue(rowA.original).localeCompare(getDateValue(rowB.original)),
    enableGlobalFilter: true,
    size: 150,
  },
  {
    id: 'state',
    accessorFn: getStateValue,
    header: ({ column }) => <SortingHeaderButton column={column}>State</SortingHeaderButton>,
    cell: ({ row }) => {
      const state = getStateValue(row.original)
      if (!state) return <EmptyValue />

      return (
        <Badge
          variant={
            state === 'Accepted'
              ? 'success'
              : state === 'Uploaded'
                ? 'info'
                : state === 'Returned' || state === 'Discarded'
                  ? 'destructive'
                  : 'outline'
          }
          className="font-normal"
        >
          {state}
        </Badge>
      )
    },
    sortingFn: (rowA, rowB) => getStateValue(rowA.original).localeCompare(getStateValue(rowB.original)),
    enableGlobalFilter: true,
    size: 120,
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => <ActivityLogActionCell item={row.original} />,
    enableGlobalFilter: false,
    size: 56,
  },
]
